import { useState } from 'react';
import { DetailedIconHomeTour, Heading, Text, TextButton, Upsell } from '@zillow/constellation';
import { useQueryClient } from '@zg-rentals/react-query5';
import { ZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing_Path } from '@zg-rentals/ts-z-calendar-client';

import TourAvailabilityWizard from '../TourAvailabilityWizard';
import useTourUpsellCookie from '../../hooks/useTourUpsellCookie';
import type { ClickStreamMetadata } from '../../constants/TouringClickStreamEvents';

type TourSchedulingUpsellProps = {
  listingAlias: string;
  modalHeaderText: string;
  onClick: () => void;
  clickStreamMetadata?: ClickStreamMetadata;
  eventId?: string;
  canAccessPostingPathTourDuration?: boolean;
};

const TourSchedulingUpsell = ({
  listingAlias,
  modalHeaderText,
  onClick,
  clickStreamMetadata,
  eventId,
  canAccessPostingPathTourDuration = false,
}: TourSchedulingUpsellProps) => {
  const queryClient = useQueryClient();

  const [displayTourAvailibilityWizard, setDisplayTourAvailibilityWizard] = useState(false);
  const { hasUpsellCookieSetForListing, setUpsellCookie } = useTourUpsellCookie(listingAlias);

  const handleUpsellClick = () => {
    setDisplayTourAvailibilityWizard(true);
    onClick();
  };

  const handleUpsellClose = () => {
    setDisplayTourAvailibilityWizard(false);
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === ZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing_Path,
    });
  };

  if (hasUpsellCookieSetForListing) return null;

  return (
    <>
      <Upsell
        heading={
          <Heading level={6} as="p">
            Book tours easier, find renters faster
          </Heading>
        }
        body={<Text>Set your tour availability now to save time.</Text>}
        media={<DetailedIconHomeTour />}
        actionButton={
          <TextButton onClick={handleUpsellClick} data-eventid={eventId}>
            Set tour availability
          </TextButton>
        }
        cardElevation={0}
        background="blue200"
        onClose={setUpsellCookie}
      />

      {displayTourAvailibilityWizard && (
        <TourAvailabilityWizard
          listingAlias={listingAlias}
          modalHeaderText={modalHeaderText}
          onClose={handleUpsellClose}
          clickStreamMetadata={clickStreamMetadata}
          canAccessPostingPathTourDuration={canAccessPostingPathTourDuration}
        />
      )}
    </>
  );
};

export default TourSchedulingUpsell;
